/**
 * Tiffany specific configuration.
 *
 * This will be merged with `src/config/index.ts`
 */

import customerDataForm from "./forms/customerData";
import customerConsentForm from "./forms/customerConsent";
import customerAdditionalInfo from "./forms/customerAdditionalInfo";
import advisorForm from "./forms/advisorForm";
import { fromDDCCustomer } from "./transforms";
import {
  CUSTOMER_DETAILS_COLUMNS,
  MARITAL_STATUS_OPTIONS,
  RESULT_FIELDS
} from "./fields";

const urlParams = new URLSearchParams(window.location.search);
const auth0Mode: string = urlParams.get("auth0")!;
const sfMode: string = urlParams.get("sf")!;

const stage: string = process.env.REACT_APP_STAGE || "dev";
const AUTH_BASE_URL = "https://tiffany.okta.com";
//"https://tiffany-sf-proxy.herokuapp.com"
const [BACKEND_BASE_URL, AUTH_CLIENT_ID, GOOGLE_MAP_KEY] =
  stage === "local"
    ? [
        "http://localhost:5000",
        "0oa15mspz5y4Mrfn6417",
        "AIzaSyDQW2Gx5Y91xmddG62yTjsILW3OotdtwFQ"
      ]
    : stage === "dev" // localhost or dev
    ? [
        "https://ddc-backend-proxy.dev.tiffany.clienteling.lvmhda.com",
        "0oa15mspz5y4Mrfn6417",
        "AIzaSyDQW2Gx5Y91xmddG62yTjsILW3OotdtwFQ"
      ]
    : stage === "staging" || stage === "sta"
    ? [
        "https://ddc-backend-proxy.dev.tiffany.clienteling.lvmhda.com",
        "0oa1bjb654bExzlvA417",
        "AIzaSyDQW2Gx5Y91xmddG62yTjsILW3OotdtwFQ"
      ]
    : [
        // prod
        "https://ddc-backend-proxy.tiffany.clienteling.lvmhda.com",
        "0oa1bj9c2n6T5gCbN417",
        "AIzaSyAnXEFZ-l8AEHWzsgn64WCSDJoe36PTfQ4"
      ];
const API_PATH = sfMode ? "/ddc/v0" : "";
const supportedLanguages = ["en", "ja"];

export default {
  id: "tiffany-jp",
  api: "/ddc/v0",
  defaultZone: "jp",
  languages: {
    supportedLanguages,
    loadPath: BACKEND_BASE_URL + "/translations/{{lng}}/{{ns}}"
  },
  services: {
    oidc: auth0Mode
      ? {
          // auth0 mock
          baseUrl: "https://dev-nvp8kyt3.us.auth0.com",
          loginRedirectEndpoint: "/authorize",
          scope: "openid offline_access",
          getTokenEndpoint: "/oauth/token",
          logoutRedirectEndpoint: "/oauth2/v2.0/logout",
          clientId: "VKanawd4BmAcHHOSYxDOyhm3fH4spfGQ",
          codeChallenge: "ThisIsntRandomButItNeedsToBe43CharactersLong",
          codeChallengeMethod: "plain",
          codeVerifier: "ThisIsntRandomButItNeedsToBe43CharactersLong",
          loginRedirectParams: {
            client_id: "VKanawd4BmAcHHOSYxDOyhm3fH4spfGQ",
            response_type: "code",
            scope: "openid offline_access"
          }
        }
      : {
          // Okta Oidc documentation: https://developer.okta.com/docs/reference/api/oidc
          // does not support refresh token from browser
          baseUrl: AUTH_BASE_URL,
          loginRedirectEndpoint: "/oauth2/v1/authorize",
          scope: "openid offline_access",
          getTokenEndpoint: "/oauth2/v1/token",
          logoutRedirectEndpoint: "/oauth2/v1/logout",
          logoutRedirectCallback: AUTH_BASE_URL,
          clientId: AUTH_CLIENT_ID,
          codeChallenge: "HPyLLwWqLbWJV0Ks7fArAD1TEGtV6JI2P8aG1EaVdjY",
          codeChallengeMethod: "S256",
          codeVerifier:
            "swbBEq~75oJQVAdgJEND6n7HNf5seQJIOuqwNE8qt33JOIbAYzsqOY.YmV_~1l_.nViF4f0xSiy87IsMh52zkHXwigneHj5FP2wcgUC0te~imSlRXsYrf4j1fH9_MwqJ"
        },
    sfdc: sfMode
      ? {
          Auth: {
            loginUrl: "https://test.salesforce.com",
            proxyUrl: "https://node-salesforce-proxy.herokuapp.com/proxy/"
          }
        }
      : null,
    api: {
      baseUrl: BACKEND_BASE_URL,
      getConfigEndpoint: API_PATH + "/ddcconfig",
      searchCustomersEndpoint: API_PATH + "/customer?search=",
      verifyAdvisorEndpoint: API_PATH + "/advisor",
      customerEndpoint: API_PATH + "/customer",
      getCustomersHistoryEndpoint: API_PATH + "/customer/history",
      customerStatusEndpoint: API_PATH + "/customer?hashcode="
    },
    googleMaps: {
      key: GOOGLE_MAP_KEY
    }
  },
  zones: {
    jp: {
      languages: supportedLanguages,
      supportedLanguages
    }
  },
  customise: {
    header: { showNotifications: false },
    verifySalesPerson: {
      resetOnActions: true
    },
    actions: {
      remoteCustomerButtonTheme: "primary"
    },
    searchCustomer: {
      enable: true,
      resultsFields: RESULT_FIELDS,
      detailsColumns: CUSTOMER_DETAILS_COLUMNS,
      enableEdit: true
    },
    customer: {
      enableAdditionalInfo: false,
      enableAdvisorForm: false,
      enableSummary: true,
      enableResultsModal: true,
      enableRemoteCustomer: true,
      summaryFields: CUSTOMER_DETAILS_COLUMNS
    },
    history: {
      enable: true,
      resultsFields: RESULT_FIELDS,
      detailsColumns: CUSTOMER_DETAILS_COLUMNS,
      enableEdit: true
    },
    fields: {
      markRequiredWithSymbol: "*",
      customFields: [
        "maritalStatus",
        "weddingDate",
        "middleName",
        "profession",
        "confirmConsent"
      ],
      picklistValues: [
        {
          name: "maritalStatus",
          options: MARITAL_STATUS_OPTIONS
        }
      ]
    }
  },
  forms: {
    schema: "form/customerFormDefinitions.json",
    fields: [
      "form/customerDataFields.json",
      "form/customerConsentFields.json",
      "form/customerThanks.json",
      "form/additionalInfoFields.json"
    ],
    customer: [
      customerDataForm,
      customerConsentForm,
      customerAdditionalInfo,
      advisorForm
    ]
  },
  transforms: {
    customer: {
      read: fromDDCCustomer
    }
  }
};
